/**
 * useSmartAlerts - Smart Audio Alert Hook
 * 
 * Session 39: Voice alerts when God Score exceeds threshold
 * Session 40: Added veto/trap respect - voice only speaks on valid trades
 * 
 * Features:
 * - Speech synthesis for voice alerts
 * - 5-minute debounce per symbol to avoid spam
 * - Respects global audio toggle
 * - Dynamic threshold support (VIX-adjusted)
 * - CRITICAL: Respects veto/trap conditions - no false baits!
 * 
 * Usage:
 *   useSmartAlerts(godScore, 'NIFTY', threshold, audioEnabled, isVetoed, vetoReason);
 */

import { useEffect, useRef } from 'react';

/**
 * Smart Alert Hook with debouncing and veto respect
 * 
 * @param {number} godScore - Current God Score value
 * @param {string} symbol - Trading symbol (NIFTY/BANKNIFTY)
 * @param {number} threshold - Dynamic threshold (default 80, VIX-adjusted)
 * @param {boolean} audioEnabled - Global audio toggle state
 * @param {boolean} isVetoed - Whether the signal is vetoed/trapped
 * @param {string} vetoReason - Reason for veto (for logging)
 */
const useSmartAlerts = (godScore, symbol, threshold = 80, audioEnabled = false, isVetoed = false, vetoReason = '') => {
  // Track last alert time per symbol to prevent spam
  const lastAlertTimeRef = useRef({});
  const previousScoreRef = useRef(0);
  const lastVetoLogRef = useRef({});  // Track veto logs to avoid spam
  
  // Debounce time: 5 minutes (300,000 ms)
  const DEBOUNCE_MS = 300000;
  const VETO_LOG_DEBOUNCE_MS = 60000;  // Log veto suppression once per minute
  
  useEffect(() => {
    // === GUARD CLAUSES ===
    
    // 1. Silent mode - audio disabled
    if (!audioEnabled) return;
    
    // 2. Score below threshold - no signal
    if (godScore < threshold) {
      // Reset previous score when falling below threshold
      // This allows a new alert when score rises above threshold again
      previousScoreRef.current = godScore;
      return;
    }
    
    // 3. CRITICAL: Respect veto/trap conditions - NEVER bait into rejected trade
    if (isVetoed) {
      const now = Date.now();
      const lastVetoLog = lastVetoLogRef.current[symbol] || 0;
      
      // Log veto suppression (debounced to avoid spam)
      if (now - lastVetoLog >= VETO_LOG_DEBOUNCE_MS) {
        console.log(`🔇 [SmartAlert] ${symbol} - Signal SUPPRESSED (Score ${godScore} >= ${threshold}, but VETOED: ${vetoReason || 'Trap Detected'})`);
        lastVetoLogRef.current[symbol] = now;
      }
      return;
    }
    
    // 4. Already alerted recently for this symbol
    const now = Date.now();
    const lastAlertTime = lastAlertTimeRef.current[symbol] || 0;
    if (now - lastAlertTime < DEBOUNCE_MS) {
      return;
    }
    
    // 5. Avoid re-alerting if score was already above threshold
    // Only alert on the CROSSING of the threshold, not every tick above it
    if (previousScoreRef.current >= threshold) {
      return;
    }
    
    // === FIRE ALERT - Signal is VALID (above threshold AND not vetoed) ===
    console.log(`🔔 [SmartAlert] ${symbol} - VALID Signal! Score ${godScore} crossed threshold ${threshold}`);
    
    try {
      // Determine direction from score context
      const direction = godScore >= 0 ? 'Buy' : 'Sell';
      
      // Create speech message
      const message = `${symbol} ${direction} Signal. Score ${Math.round(godScore)}`;
      
      // Cancel any previous speech
      window.speechSynthesis.cancel();
      
      // Create and configure utterance
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.rate = 1.0;  // Normal speed
      utterance.pitch = 1.0; // Normal pitch
      utterance.volume = 0.8; // 80% volume
      
      // Speak the alert
      window.speechSynthesis.speak(utterance);
      
      // Update last alert time for this symbol
      lastAlertTimeRef.current[symbol] = now;
      
      console.log(`🔊 [SmartAlert] Speaking: "${message}"`);
      
    } catch (error) {
      console.warn('[SmartAlert] Speech synthesis failed:', error);
    }
    
    // Update previous score
    previousScoreRef.current = godScore;
    
  }, [godScore, symbol, threshold, audioEnabled, isVetoed, vetoReason]);
  
  // Return useful info for debugging/display
  return {
    isAlertPending: audioEnabled && godScore >= threshold && !isVetoed,
    isVetoSuppressed: audioEnabled && godScore >= threshold && isVetoed,
    lastAlertTime: lastAlertTimeRef.current[symbol] || null,
    canAlert: audioEnabled && !isVetoed && (Date.now() - (lastAlertTimeRef.current[symbol] || 0)) >= DEBOUNCE_MS,
  };
};

export default useSmartAlerts;
